import { type VendorResource, VendorsController_list_v1 } from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Switch } from '@repo/ui/components/switch';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconCircleCheck, IconPlus } from '@tabler/icons-react';
import { useCallback, useDeferredValue, useEffect, useState } from 'react';

import { useApiClient } from '@/api/api-client';

import { Pager } from '../back-office/Pager';
import { type PageState, ROWS_PER_PAGE } from '../back-office/pagination';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { VendorDialog } from './VendorDialog';

/**
 * The suppliers the park pays (#247): `GET /v1/vendors`, owner and manager.
 *
 * **Paged, not read whole.** Vendors grow with use and never shrink — a retired
 * one stays — which is the case `docs/ui-conventions.md` gives `Pager` for. The
 * name search and the retired toggle are the API's, so a filter is a new first
 * page rather than a narrowing of the one on screen.
 *
 * The name opens the vendor, as an outlet's does; a retired one opens read-only.
 */

type ListState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly vendors: readonly VendorResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

const COLUMNS_WITHOUT_STATUS = 3;

export function Vendors() {
  const client = useApiClient();

  const [includeRetired, setIncludeRetired] = useState(false);
  const [search, setSearch] = useState('');
  // Deferred so typing stays responsive; each change still aborts the last read.
  const query = useDeferredValue(search.trim());

  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<PageState>({ offset: 0, limit: ROWS_PER_PAGE, total: 0 });
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  // `null` while closed; `{ vendor: null }` while creating one.
  const [opened, setOpened] = useState<{ vendor: VendorResource | null } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(VendorsController_list_v1, {
        query: {
          offset,
          limit: ROWS_PER_PAGE,
          ...(query === '' ? {} : { search: query }),
          ...(includeRetired ? { includeRetired: true } : {}),
        },
        signal: controller.signal,
      })
      .then((answer) => {
        if (!live) return;
        setState({ kind: 'loaded', vendors: answer.items });
        setPage({ offset: answer.offset, limit: answer.limit, total: answer.total });
      })
      .catch((error: unknown) => {
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, query, includeRetired, offset, reloads]);

  const columns = includeRetired ? COLUMNS_WITHOUT_STATUS + 1 : COLUMNS_WITHOUT_STATUS;

  return (
    <Screen
      title="Vendors"
      action={
        <Button onClick={() => setOpened({ vendor: null })}>
          <IconPlus />
          New vendor
        </Button>
      }
      aside={
        <>
          {state.kind === 'loaded' && (
            <ScreenFact label="Matching">
              <span className="font-mono numeric">{page.total}</span>{' '}
              {page.total === 1 ? 'vendor' : 'vendors'}
              {includeRetired && ', retired included'}
            </ScreenFact>
          )}

          <div className="space-y-2">
            <Label htmlFor="vendor-search" className="text-sm">
              Name contains
            </Label>
            <Input
              id="vendor-search"
              type="search"
              placeholder="Balaji"
              autoComplete="off"
              maxLength={100}
              className="h-9 bg-card"
              value={search}
              onChange={(event) => {
                // A filter change is a new first page: an offset past the end
                // of a shorter result would show an empty page that is not.
                setSearch(event.target.value);
                setOffset(0);
              }}
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="show-retired-vendors"
              aria-labelledby="show-retired-vendors-label"
              checked={includeRetired}
              onCheckedChange={(checked) => {
                setIncludeRetired(checked);
                setOffset(0);
              }}
            />
            <Label
              id="show-retired-vendors-label"
              htmlFor="show-retired-vendors"
              className="cursor-pointer text-sm"
            >
              Show retired vendors
            </Label>
          </div>
        </>
      }
    >
      {notice !== null && (
        <Alert role="status">
          <IconCircleCheck />
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      <ScreenTable label="Vendors" columns="stated">
        <TableHeader>
          <TableRow>
            <TableHead className="w-80">Name</TableHead>
            <TableHead className="w-44">Phone</TableHead>
            <TableHead className="w-48">GSTIN</TableHead>
            {includeRetired && <TableHead className="w-32">Status</TableHead>}
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={columns} />}

        {state.kind === 'failed' && (
          <TableNotice columns={columns}>
            <LoadFailed error={state.error} onRetry={reload} />
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (state.vendors.length === 0 ? (
            <TableNotice columns={columns}>
              <EmptyState
                title={query === '' ? 'No vendors yet' : 'No vendor matches that name'}
                description={
                  query !== ''
                    ? 'Try fewer letters, or turn on Show retired vendors.'
                    : 'New vendor adds one.'
                }
              />
            </TableNotice>
          ) : (
            <TableBody>
              {state.vendors.map((vendor) => (
                <VendorRow
                  key={vendor.id}
                  vendor={vendor}
                  showStatus={includeRetired}
                  onOpen={() => setOpened({ vendor })}
                />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {state.kind === 'loaded' && <Pager state={page} label="vendors" onOffsetChange={setOffset} />}

      {opened !== null && (
        <VendorDialog
          vendor={opened.vendor}
          onOpenChange={(open) => {
            if (!open) setOpened(null);
          }}
          onSaved={(vendor, created) => {
            setOpened(null);
            setNotice(`${vendor.name} has been ${created ? 'added' : 'updated'}.`);
            reload();
          }}
          onRetired={(vendor) => {
            setOpened(null);
            setNotice(`${vendor.name} has been retired.`);
            reload();
          }}
        />
      )}
    </Screen>
  );
}

function VendorRow({
  vendor,
  showStatus,
  onOpen,
}: {
  vendor: VendorResource;
  showStatus: boolean;
  onOpen: () => void;
}) {
  const retired = vendor.retiredAt !== null;

  return (
    <TableRow className={retired ? 'text-muted-foreground' : undefined}>
      <TableCell className="whitespace-normal">
        <button
          type="button"
          className="rounded-sm text-left underline-offset-4 outline-none hover:underline focus-visible:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          onClick={onOpen}
        >
          <span className={retired ? undefined : 'text-foreground'}>{vendor.name}</span>
        </button>
      </TableCell>
      <TableCell className="font-mono numeric">{vendor.phone ?? ''}</TableCell>
      {/* Stored upper-case by the API; upper-cased here too so a row never
          depends on that. */}
      <TableCell className="font-mono tracking-wider numeric">
        {vendor.gstin?.toUpperCase() ?? ''}
      </TableCell>
      {showStatus && (
        <TableCell>
          {retired ? (
            <Badge variant="outline">Retired</Badge>
          ) : (
            <span className="text-muted-foreground">Active</span>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
